import React, { createContext, useContext, useState } from 'react';
import usePage from '@dashboard/hooks/usePage';
import { useFields } from './FieldsContext';
import { useQueryClient } from '@tanstack/react-query';
import request from '@dashboard/services/request';
import { apiUrl } from '@dashboard/services/env';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { pageKey } from '@dashboard/lib/queryKeys';
import { confirm } from '@dashboard/lib/confirm';
import { uniqueId } from 'lodash';
import { elementFieldTypeEnums } from '@dashboard/services/pageFieldTypes';

const FieldEditContext = createContext();

const reorderFields = async (sellerId, pageId, data, token) =>
  request(`${apiUrl}/sellers/${sellerId}/pages/${pageId}/fields/reorder`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export function FieldEditProvider({ children }) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const { state: fields, dispatch } = useFields();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddingField, setIsAddingField] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  async function onReorderFields(reorderedFields) {
    setIsReordering(true);
    const originalFields = [...fields];

    dispatch({ type: 'set', values: reorderedFields });

    const fieldIds = reorderedFields.map(f => f._id);

    const response = await reorderFields(sellerId, page._id, { order: fieldIds }, token);

    // revert fields and show error
    if (!response.data) {
      dispatch({ type: 'set', values: originalFields });
      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    } else {
      dispatch({ type: 'set', values: reorderedFields });
      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    }
    setIsReordering(false);
  }

  async function setIsEditingField(isEditing, fieldId) {
    // if adding field, don't allow opening other fields
    // or closing the current one
    if (isAddingField) return;

    let proceed = true;
    // if not editing and has unsaved changes, ask for confirmation before closing
    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setHasUnsavedChanges(false);

    dispatch({
      type: 'set',
      // open current field and close all others
      values: fields.map(f => ({
        ...f,
        isEditing: f._id === fieldId ? isEditing : false,
      })),
    });
  }

  function onAddField() {
    const id = uniqueId();

    dispatch({
      type: 'add',
      values: [
        {
          element: elementFieldTypeEnums.TEXT,
          key: id,
          showHideLogic: {
            enabled: false,
            comparison: 'IS',
            value: '',
            element: {
              elementType: 'field',
              elementId: '',
              elementTitle: '',
            },
          },
          isEditing: true,
          isNew: true,
        },
      ],
    });

    setIsAddingField(true);
  }

  return (
    <FieldEditContext.Provider
      value={{
        onAddField,
        isAddingField,
        setIsAddingField,
        onReorderFields,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setIsEditingField,
        isReordering,
      }}
    >
      {children}
    </FieldEditContext.Provider>
  );
}

export const useFieldEdit = () => useContext(FieldEditContext);
